import {
  getCanonicalLastRead,
  getLastReadChapter,
  getRecentChapters,
  readingTimestamp,
} from "../bible/readingHistoryStorage";
import { getReadChapters } from "../components/ChapterProgress/useChapterProgress";
import { getReadingStorageKey, setReadingStorageUser } from "../bible/readingStorageScope";
import {
  d1GetReadingRecords,
  d1SaveReadingRecords,
  waitForAuthToken,
} from "../services/d1Client";

const STORAGE_LAST_READ = "bible_last_read";
const STORAGE_RECENT_CHAPTERS = "bible_recent_chapters";
const STORAGE_READ_CHAPTERS = "bible_read_chapters";

const inflightSync = new Map();

const writeLocalReading = (uid, { lastRead, recentChapters, readChapters }) => {
  if (lastRead) {
    localStorage.setItem(getReadingStorageKey(STORAGE_LAST_READ, uid), JSON.stringify(lastRead));
  }
  localStorage.setItem(
    getReadingStorageKey(STORAGE_RECENT_CHAPTERS, uid),
    JSON.stringify(recentChapters || [])
  );
  localStorage.setItem(
    getReadingStorageKey(STORAGE_READ_CHAPTERS, uid),
    JSON.stringify(readChapters || {})
  );
  window.dispatchEvent(new Event("bible_last_read_changed"));
  window.dispatchEvent(new Event("bible_read_status_changed"));
};

/**
 * Sync reading records between local storage and Cloudflare D1.
 * Uses only this account's scoped keys plus its own cloud row.
 */
export const syncCloudReadingRecords = async (uid) => {
  if (!uid || uid === "local_guest_user" || uid === "local_user") return null;
  if (inflightSync.has(uid)) return inflightSync.get(uid);

  const pending = (async () => {
    setReadingStorageUser(uid);
    const token = await waitForAuthToken();
    if (!token) {
      console.warn("Skipping reading sync until auth token is ready");
      return null;
    }

    try {
      const syncStartedAt = Date.now();
      const cloudData = await d1GetReadingRecords(uid);

      const localLastRead = getCanonicalLastRead(uid) || getLastReadChapter(uid);
      const localRecent = getRecentChapters(uid);
      const localReadMap = getReadChapters(uid);

      let mergedLastRead = localLastRead;
      if (cloudData?.lastRead) {
        if (
          !localLastRead ||
          readingTimestamp(cloudData.lastRead) > readingTimestamp(localLastRead)
        ) {
          mergedLastRead = cloudData.lastRead;
        }
      }

      const recentMap = new Map();
      (cloudData?.recentChapters || []).forEach((item) => {
        if (item && item.chapterId) {
          recentMap.set(item.chapterId, item);
        }
      });
      (localRecent || []).forEach((item) => {
        if (item && item.chapterId) {
          const existing = recentMap.get(item.chapterId);
          if (!existing || readingTimestamp(item) > readingTimestamp(existing)) {
            recentMap.set(item.chapterId, item);
          }
        }
      });
      const mergedRecent = Array.from(recentMap.values())
        .sort((a, b) => readingTimestamp(b) - readingTimestamp(a))
        .slice(0, 15);

      if (!mergedLastRead && mergedRecent[0]) {
        mergedLastRead = mergedRecent[0];
      } else if (mergedLastRead && mergedRecent[0]) {
        if (readingTimestamp(mergedRecent[0]) > readingTimestamp(mergedLastRead)) {
          mergedLastRead = mergedRecent[0];
        }
      }

      // A chapter opened while this sync was in flight must win over a stale merge,
      // even if the cloud row carries a newer-looking timestamp.
      const latestLocal = getCanonicalLastRead(uid) || getLastReadChapter(uid);
      const localTouchedDuringSync =
        latestLocal?.chapterId && readingTimestamp(latestLocal) >= syncStartedAt;
      if (
        latestLocal?.chapterId &&
        (localTouchedDuringSync ||
          readingTimestamp(latestLocal) > readingTimestamp(mergedLastRead))
      ) {
        mergedLastRead = latestLocal;
        const alreadyListed = mergedRecent.some(
          (item) => item.chapterId === latestLocal.chapterId
        );
        if (!alreadyListed) {
          mergedRecent.unshift(latestLocal);
        } else {
          const idx = mergedRecent.findIndex(
            (item) => item.chapterId === latestLocal.chapterId
          );
          if (idx >= 0) mergedRecent[idx] = latestLocal;
          mergedRecent.sort((a, b) => readingTimestamp(b) - readingTimestamp(a));
        }
      }

      const mergedReadMap = { ...(cloudData?.readChapters || {}), ...(localReadMap || {}) };

      writeLocalReading(uid, {
        lastRead: mergedLastRead,
        recentChapters: mergedRecent,
        readChapters: mergedReadMap,
      });

      const payload = {
        lastRead: mergedLastRead || null,
        recentChapters: mergedRecent,
        readChapters: mergedReadMap,
        readCount: Object.keys(mergedReadMap).filter((k) => Boolean(mergedReadMap[k])).length,
      };

      try {
        await d1SaveReadingRecords(uid, payload);
      } catch (saveErr) {
        console.warn("Could not save merged reading records to D1:", saveErr.message);
      }

      return payload;
    } catch (err) {
      console.warn("Failed to sync reading records with Cloudflare D1:", err);
      return null;
    }
  })();

  inflightSync.set(uid, pending);
  try {
    return await pending;
  } finally {
    inflightSync.delete(uid);
  }
};

/**
 * Push current reading state directly to Cloudflare D1 if user is signed in.
 */
export const pushReadingProgressToCloud = async (uid) => {
  if (!uid || uid === "local_guest_user" || uid === "local_user") return;
  const token = await waitForAuthToken();
  if (!token) return;
  try {
    const lastRead = getCanonicalLastRead(uid) || getLastReadChapter(uid);
    const recentChapters = getRecentChapters(uid);
    const readChapters = getReadChapters(uid);

    await d1SaveReadingRecords(uid, {
      lastRead: lastRead || null,
      recentChapters: recentChapters || [],
      readChapters: readChapters || {},
      readCount: Object.keys(readChapters || {}).filter((k) => Boolean(readChapters[k])).length,
    });
  } catch (e) {
    console.warn("Could not push reading state to Cloudflare D1:", e);
  }
};
